import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSyncAlt } from 'react-icons/fa';

import { sampleAvatar } from '~/pages/ProfileSetting/sampleAvatar';
import styles from './AvatarCarousel.module.css';

// Horizontal drag distance (px) past which a pointer gesture counts as a swipe.
const SWIPE_THRESHOLD = 40;
// Horizontal spacing (px) between adjacent heads in the coverflow.
const STEP = 112;
// Number of heads rendered on each side of the centred one.
const WINDOW = 2;

export type AvatarCarouselProps = {
    /** Pre-selected avatar (e.g. the player's current one) — becomes the first head. */
    initialAvatar?: string;
    /** Called with the selected avatar URL whenever it changes (and once on mount). */
    onChange: (avatar: string) => void;
};

/**
 * Coverflow avatar picker shared by the profile and in-game settings screens.
 * Neighbour heads peek on the sides to signal the carousel is swipeable; the
 * centred head fades out toward the swipe direction while the neighbour grows in.
 */
export function AvatarCarousel({ initialAvatar, onChange }: AvatarCarouselProps) {
    const { t } = useTranslation();

    const [avatars, setAvatars] = useState<string[]>(() => {
        const seed = [sampleAvatar(), sampleAvatar(), sampleAvatar(), sampleAvatar(), sampleAvatar()];
        if (initialAvatar) seed[0] = initialAvatar;
        return seed;
    });
    const [index, setIndex] = useState(0);

    // Notify the parent of the selected avatar (on mount + every change).
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    useEffect(() => {
        onChangeRef.current(avatars[index]);
    }, [index, avatars]);

    // Keep the right-side peek heads rendered ahead of the index (no blank on swipe).
    useEffect(() => {
        setAvatars((prev) => {
            const need = index + WINDOW + 1;
            if (prev.length >= need) return prev;
            const next = [...prev];
            while (next.length < need) next.push(sampleAvatar());
            return next;
        });
    }, [index]);

    const [dragOffset, setDragOffset] = useState(0);
    const [dragTransition, setDragTransition] = useState(false);
    const dragRef = useRef({ startX: 0, dragging: false, moved: false });

    const goTo = (target: number) => {
        // Clamp to the rendered range; the effect above keeps a buffer ahead so
        // forward navigation always lands on an existing (defined) head.
        const clamped = Math.min(avatars.length - 1, Math.max(0, target));
        if (clamped === index) return;
        setDragTransition(true);
        setIndex(clamped);
    };
    const goNext = () => goTo(index + 1);

    const onPointerDown = (e: React.PointerEvent) => {
        dragRef.current = { startX: e.clientX, dragging: true, moved: false };
        setDragTransition(false);
    };
    const onPointerMove = (e: React.PointerEvent) => {
        if (!dragRef.current.dragging) return;
        let dx = e.clientX - dragRef.current.startX;
        if (Math.abs(dx) > 8) dragRef.current.moved = true;
        // Rubber-band when there's nothing before the first head.
        if (index === 0 && dx > 0) dx *= 0.35;
        setDragOffset(Math.max(-STEP, Math.min(STEP, dx)));
    };
    const onPointerUp = (e: React.PointerEvent) => {
        if (!dragRef.current.dragging) return;
        const dx = e.clientX - dragRef.current.startX;
        dragRef.current.dragging = false;
        setDragTransition(true);
        if (dx <= -SWIPE_THRESHOLD) goTo(index + 1);
        else if (dx >= SWIPE_THRESHOLD) goTo(index - 1);
        setDragOffset(0);
    };
    const onPointerCancel = () => {
        dragRef.current.dragging = false;
        setDragTransition(true);
        setDragOffset(0);
    };
    // Tap on a peek head — ignored if the gesture was actually a swipe.
    const onTapHead = (i: number) => {
        if (!dragRef.current.moved) goTo(i);
    };

    return (
        <div className={styles.root}>
            <div
                className={styles.carousel}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerCancel}
            >
                {avatars.map((src, i) => {
                    if (i < index - WINDOW || i > index + WINDOW) return null;
                    const x = (i - index) * STEP + dragOffset;
                    const ax = Math.abs(x);
                    const scale = Math.max(0.6, 1 - (ax / STEP) * 0.34);
                    const opacity = Math.max(0.2, 1 - (ax / STEP) * 0.55);
                    const ringOpacity = Math.max(0, 1 - ax / STEP);
                    return (
                        <button
                            type="button"
                            key={i}
                            className={styles.head}
                            onClick={() => onTapHead(i)}
                            tabIndex={i === index ? -1 : 0}
                            aria-label={t('global.settings.unguy')}
                            style={{
                                transform: `translateX(${x}px) scale(${scale})`,
                                opacity,
                                zIndex: Math.round(200 - ax),
                                transition: dragTransition ? undefined : 'none',
                            }}
                        >
                            <span className={styles.headRing} style={{ opacity: ringOpacity }} aria-hidden />
                            <span className={styles.headFace}>
                                <img src={src} alt="" draggable={false} />
                            </span>
                        </button>
                    );
                })}
            </div>

            <button type="button" className={styles.shuffle} onClick={goNext}>
                <FaSyncAlt />
                {t('global.settings.shuffleAvatar')}
            </button>
        </div>
    );
}
